import type { ServiceItem } from '@/cms/fields/types/checkout-page'
import type { BookingDraft, CheckoutParticipantAgeCategory, PhoneNumber, ServiceMappers } from '../../domain/types'
import type { CheckoutPriceOverviewSubItems } from '../../domain/types/priceOverview'
import {
  type APIBookingDraft,
  type APIBookingDraftPriceOverviewItem,
  type APIBookingDraftPriceOverviewSubItems,
  type APIBookingDraftServiceOverview,
  type ServiceRenderType,
  ServiceRenderTypeEnum,
} from '@/scopes/checkout/infrastructure/api/types'
import FeatureFlagClient from '@/scopes/common/infrastructure/feature-flags/featureFlagClient'
import { countryData } from '@sunwebgroup/ui-components'
import { format } from 'date-fns'
import { ServiceType } from '../../domain/types'

export interface BookingDraftResponseMapper {
  map: (bookingDraft: APIBookingDraft, configData?: ServiceItem[]) => BookingDraft
}

interface Dependencies {
  serviceMappers: ServiceMappers
}

export function createBookingDraftResponseMapper(dependencies: Dependencies): BookingDraftResponseMapper {
  const { serviceMappers: mappers } = dependencies

  const map = (bookingDraft: APIBookingDraft, configData?: ServiceItem[]) => {
    const activateGenericServiceDynamicStep1 = FeatureFlagClient.isActive('ACTIVATE_GENERIC_SERVICE_DYNAMIC_STEP_1')
    const missingServices = configData?.filter(service => service.type === 'GenericService') ?? []

    const mapStandardServices = Object.entries(mappers).reduce((acc, [serviceType, serviceMapper]) => {
      const mappedService = serviceMapper.map(bookingDraft)
      return mappedService ? { ...acc, [serviceType]: mappedService } : acc
    }, {})

    const mappedMissingServices
      = activateGenericServiceDynamicStep1 && missingServices.length
        ? missingServices.reduce(
            (acc, service) => {
              const serviceData = bookingDraft.services.find(item => item.aName === service.aName)
              if (!serviceData || !serviceData.aName)
                return acc

              const mapByRenderType = (
                renderType: ServiceRenderType,
                acc2: {
                  genericServicePerBooking: object
                  genericServicePerParticipant: object
                  genericServicePerRoom: object
                },
              ) => {
                const type = getGenericServiceType(renderType)
                const mapper = mappers[type]
                if (!mapper)
                  return acc2
                const mapped = mapper?.map(bookingDraft, serviceData.aName)
                return mapped
                  ? {
                      ...acc2,
                      [type]: {
                        ...(acc2[type] || {}),
                        [mapped.id]: mapped,
                      },
                    }
                  : acc2
              }
              if (
                service.serviceConfigurations?.showToggleForAllParticipants
                && service.renderType !== ServiceRenderTypeEnum.PerRoom
              ) {
                const alternateRenderTypeService
                  = service.renderType === ServiceRenderTypeEnum.PerParticipant
                    ? ServiceRenderTypeEnum.PerBooking
                    : ServiceRenderTypeEnum.PerParticipant
                const alternateService = mapByRenderType(alternateRenderTypeService, acc)
                return mapByRenderType(service.renderType as ServiceRenderType, alternateService)
              }

              return mapByRenderType(service.renderType as ServiceRenderType, acc)
            },
            {
              [ServiceType.GenericServicePerBooking]: {},
              [ServiceType.GenericServicePerParticipant]: {},
              [ServiceType.GenericServicePerRoom]: {},
            },
          )
        : {}

    return {
      services: {
        ...mapStandardServices,
        ...mappedMissingServices,
      },
      basketInformation: mapBasket(bookingDraft),
      isRebooking: bookingDraft.isRebooking,
      messages: mapConditionalMessages(bookingDraft),
      navigation: mapNavigation(bookingDraft),
      participants: mapParticipants(bookingDraft),
      rooms: mapRooms(bookingDraft),
      error: mapError(bookingDraft),
      groupInformation: mapGroupInformation(bookingDraft),
      draftId: bookingDraft.draftId,
      analytics: mapECommerceAnalytics(bookingDraft),
      summaries: mapSummaries(bookingDraft),
      ...(bookingDraft.bookingUsps && { usps: mapUsps(bookingDraft) }),
      configurations: mapConfigurations(bookingDraft),
      ...(bookingDraft.priceOverviews && { priceOverviews: mapPriceOverviews(bookingDraft) }),
      couponCodeSuccess: textToBoolean(bookingDraft.consequences?.couponcodeSuccess ?? ''),
    } as BookingDraft
  }

  return {
    map,
  }
}

function textToBoolean(text: string) {
  if (typeof text !== 'string')
    return false
  return text.trim().toLowerCase() === 'true'
}

function getGenericServiceType(renderType: ServiceRenderType) {
  switch (renderType) {
    case ServiceRenderTypeEnum.PerParticipant:
      return ServiceType.GenericServicePerParticipant
    case ServiceRenderTypeEnum.PerBooking:
      return ServiceType.GenericServicePerBooking
    case ServiceRenderTypeEnum.PerRoom:
      return ServiceType.GenericServicePerRoom
    default:
      return ServiceType.GenericServicePerBooking
  }
}
function mapBasket(bookingDraft: APIBookingDraft) {
  const { accommodationInfo, bookingInfo } = bookingDraft
  if (!accommodationInfo || !bookingInfo)
    return {}

  return {
    accommodationName: accommodationInfo.name,
    rating: accommodationInfo.numberOfStars,
    location: accommodationInfo.location
      .map(location => location)
      .reverse()
      .join(', '),
    imageUrl: accommodationInfo.mainImage?.original,
    servicesIncluded: bookingInfo.includedServices,
    departureDate: bookingInfo.departureDate,
    duration: bookingInfo.duration,
    occupancy: bookingInfo.occupancy,
    totalPrice: bookingInfo.totalPrice,
    avgPrice: bookingInfo.avgPrice,
    servicesOverview: mapOverviewServices(bookingDraft.overviewServices ?? []),
    rebookingWarning: bookingInfo.rebookingWarning,
    originalBookingPaidAmount: bookingInfo.originalBookingPaidAmount,
    totalPackagePrice: bookingInfo.totalPackagePrice,
    avgTotalPackagePrice: bookingInfo.avgTotalPackagePrice,
  }
}

function mapOverviewServices(overviewServices: APIBookingDraftServiceOverview[]) {
  if (!overviewServices)
    return []

  return overviewServices.map(service => ({
    isCollapsedByDefault: service.isCollapsedByDefault,
    title: service.header,
    icon: service.icon,
    serviceAName: service?.serviceAName ?? '',
    ariaLabel: service.ariaLabel,
    lines:
      service?.lines?.map(line => ({
        isTitle: line.isTitle,
        text: line.text,
      })) ?? [],
  }))
}

function mapConditionalMessages(bookingDraft: APIBookingDraft) {
  const { conditionalMessages } = bookingDraft
  if (!conditionalMessages)
    return {}

  return conditionalMessages.reduce((acc, messageGroup) => {
    return {
      ...acc,
      [messageGroup.componentId]: {
        id: messageGroup.componentId,
        banners:
          messageGroup.messages?.map(message => ({
            title: message.title,
            text: message.text,
            type: message.type.toLowerCase(),
          })) ?? [],
      },
    }
  }, {})
}

function mapNavigation(bookingDraft: APIBookingDraft) {
  const { nextButtonText, nextButtonUrl } = bookingDraft.navigation ?? {}
  const { text, url: previousUrl } = bookingDraft?.previousPage ?? {}

  const nextStep
    = nextButtonText && nextButtonUrl
      ? {
          nextStep: {
            text: nextButtonText,
            url: `${nextButtonUrl}?sid=${bookingDraft.draftId}`,
          },
        }
      : {}

  const previousStep
    = text && previousUrl
      ? {
          previousStep: {
            text,
            url: previousUrl,
          },
        }
      : {}

  return { ...nextStep, ...previousStep }
}

function mapParticipants({ participants }: APIBookingDraft) {
  if (!participants)
    return {}
  return (
    participants.reduce(
      (acc, participant) => ({
        ...acc,
        [participant.id.toString()]: {
          id: participant.id.toString(),
          name: participant.name || participant.firstName,
          birthdate: participant.birthdate,
          roomId: participant.roomId.toString(),
          ageCategory: participant.ageCategory,
          alternativeName: participant.alternativeName,
          ...(participant.mobilePhoneNumber && { mobilePhoneNumber: mapPhoneNumber(participant.mobilePhoneNumber) }),
          ...(participant.emergencyPhoneNumber && {
            emergencyPhoneNumber: mapPhoneNumber(participant.emergencyPhoneNumber),
          }),
          ...(participant.title && { title: participant.title === 'NotSet' ? '' : participant.title.toLowerCase() }),
          ...(participant.email && { email: participant.email }),
          ...(participant.surname && { surname: participant.surname }),
        },
      }),
      {},
    ) ?? {}
  )
}

function mapPhoneNumber(phoneNumber: string): PhoneNumber | null {
  const sanitizedNumber = phoneNumber.startsWith('+') ? phoneNumber.slice(1) : phoneNumber

  for (const country of countryData) {
    if (sanitizedNumber.startsWith(country.code)) {
      const localNumber = sanitizedNumber.slice(country.code.length)

      const regex = new RegExp(country.pattern)
      if (regex.test(localNumber)) {
        return { countryCode: country.code, phoneNumber: localNumber }
      }
    }
  }

  return null
}

function mapRooms({ rooms }: APIBookingDraft) {
  if (!rooms)
    return {}
  return (
    rooms.reduce(
      (acc, room) => ({
        ...acc,
        [room.id.toString()]: {
          id: room.id.toString(),
          name: room.name,
          participantIds: room.participantIds.map(participantId => participantId.toString()),
        },
      }),
      {},
    ) ?? {}
  )
}

function mapError(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.error)
    return null
  if (!bookingDraft.error.hasError)
    return null

  return {
    hasError: bookingDraft.error.hasError,
    message: bookingDraft.error.description ?? bookingDraft.error.errorMessage ?? '',
    redirectUrl: bookingDraft.error.redirectUrlToSearchPage ?? bookingDraft.error.redirectUrlToAccoPage ?? '',
    redirectTo: bookingDraft.error.redirectUrlToSearchPage ? 'search' : 'acco',
    errorCode: bookingDraft.error.errorCode ?? '',
  }
}

function mapGroupInformation(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.groupInfo)
    return null

  return {
    name: bookingDraft.groupInfo?.name,
    masterBookingNumber: bookingDraft.groupInfo.masterBookingNumber?.toString(),
    numberOfParticipants: bookingDraft.groupInfo.size?.toString(),
  }
}

function mapSummaries({ summaryComponents }: APIBookingDraft) {
  if (!summaryComponents)
    return null
  return summaryComponents
    .filter(service => service.type === 'generic')
    .map(service => ({
      title: service.title,
      icon: service.icon,
      lines: service?.items ?? [],
    }))
}

function mapUsps(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.bookingUsps)
    return {}
  return bookingDraft.bookingUsps.reduce((acc, usp) => {
    return {
      ...acc,
      [usp.componentId]: {
        id: usp.componentId,
        usps: usp.items.map(item => ({
          text: item.text,
          ...(item.subtitle && { subtitle: item.subtitle }),
          ...(item.icon && { icon: item.icon }),
        })),
      },
    }
  }, {})
}

function mapECommerceAnalytics(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.eCommerceCheckout)
    return null
  const product = bookingDraft.eCommerceCheckout.products[0]

  return {
    step: bookingDraft.eCommerceCheckout.step,
    accoId: bookingDraft.eCommerceCheckout.accoId.toString(),
    accoName: product.name,
    accoBrand: bookingDraft.eCommerceCheckout.brand,
    breadcrumbs: product.category,
    badges: product.badges ?? '',
    countryId: bookingDraft.eCommerceCheckout.locationId ?? 0,
    accoStars: Number(product.accoStars),
    transportType: product.transport,
    boardType: product.boardType,
    duration: product.duration ?? '',
    rating: Number(product.rating),
    mandatoryPayment: bookingDraft.eCommerceCheckout.mandatoryPayment,
    partyComposition: bookingDraft.eCommerceCheckout.partyComposition,
    totalGuests: bookingDraft.eCommerceCheckout.totalGuests ?? 0,
    departureDate: bookingDraft.eCommerceCheckout.departureDate,
    price: product.price,
    roomType: product.roomType,
    totalStepPrice: bookingDraft.eCommerceCheckout.totalPrice,
    campaignId: bookingDraft.eCommerceCheckout.campaignCode ?? '',
  }
}

function mapConfigurations(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.ageCategoryRestrictions)
    return {}

  const configurations = { ageRestrictions: {} }
  configurations.ageRestrictions = bookingDraft.ageCategoryRestrictions.reduce((acc, ageCategoryRestriction) => {
    return {
      ...acc,
      [ageCategoryRestriction.type as CheckoutParticipantAgeCategory]: {
        min: format(new Date(ageCategoryRestriction.dateRange.startDate), 'yyyy-MM-dd'),
        max: format(new Date(ageCategoryRestriction.dateRange.endDate), 'yyyy-MM-dd'),
      },
    }
  }, {})

  return configurations
}

function mapPriceOverviews(bookingDraft: APIBookingDraft) {
  if (!bookingDraft.priceOverviews)
    return {}

  return bookingDraft.priceOverviews.reduce((acc, priceOverview) => {
    return {
      ...acc,
      [priceOverview.componentId]: {
        componentId: priceOverview.componentId,
        warningMessage: priceOverview.warningMessage ?? '',
        mandatoryCostHeader: priceOverview.mandatoryCostsHeader ?? '',
        mandatoryCosts: priceOverview.mandatoryCosts ?? [],
        ...(priceOverview.priceOverviewItems && { items: _mapPriceOverviewItems(priceOverview.priceOverviewItems) }),
      },
    }
  }, {})
}

function _mapPriceOverviewItems(items: APIBookingDraftPriceOverviewItem[]) {
  if (!items)
    return []

  return items.map(item => ({
    value: item.price,
    concept: item.concept,
    total: item.isTotal,
    isDiscount: item.isDiscount,
    isInfo: item.isInfo,
    ...(item.children && { subitems: _mapPriceOverviewSubItems(item.children) }),
  }))
}

function _mapPriceOverviewSubItems(subitems: APIBookingDraftPriceOverviewSubItems[]): CheckoutPriceOverviewSubItems[] {
  return subitems.map(subitem => ({
    concept: subitem.concept,
    isInfo: subitem.isInfo,
    value: subitem.price || '',
    subitems: subitem.children ? _mapPriceOverviewSubItems(subitem.children) : [],
  }))
}
